import type { BlockProgram } from '../behavior/types';
import { AuthApiError, apiBaseUrl } from './authApi';

/**
 * Server-side project mirror. The local IndexedDB store is still the
 * source of truth for unpublished work; this is the cloud copy a
 * signed-in kid creates when they click "Sync to my account".
 *
 * No samples or model weights — only metadata + the kid's block
 * program — until the gallery publish flow lands.
 */

export interface ServerProject {
  id: string;
  name: string;
  kind: 'image' | 'audio' | 'pose';
  block_program: BlockProgram | null;
  created_at: string;
  updated_at: string;
}

export interface CreateServerProjectBody {
  name: string;
  kind: ServerProject['kind'];
  block_program: BlockProgram | null;
}

export interface UpdateServerProjectBody {
  name?: string;
  block_program?: BlockProgram | null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, { ...init, headers, credentials: 'include' });
  if (response.status === 204) {
    return undefined as T;
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Empty body — fall through with null.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export async function listServerProjects(): Promise<ServerProject[]> {
  return request<ServerProject[]>('/projects');
}

export async function createServerProject(body: CreateServerProjectBody): Promise<ServerProject> {
  return request<ServerProject>('/projects', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getServerProject(id: string): Promise<ServerProject> {
  return request<ServerProject>(`/projects/${encodeURIComponent(id)}`);
}

export async function updateServerProject(
  id: string,
  body: UpdateServerProjectBody,
): Promise<ServerProject> {
  return request<ServerProject>(`/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteServerProject(id: string): Promise<void> {
  return request<void>(`/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
